import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import { apiRequest } from '../api/client'
import type {
    Feedback,
    FeedbackCategory,
    FeedbackPage,
    FeedbackStatus,
} from '../types/api'

const FEEDBACK_PAGE_LIMIT = 20

/** POST /feedback — FeedbackDialog im UserMenu (nur eingeloggte Nutzer). */
export function useCreateFeedback() {
    return useMutation({
        mutationFn: (body: {
            category: FeedbackCategory
            message: string
            path: string
        }) => apiRequest<Feedback>('/feedback', { method: 'POST', body }),
    })
}

/** Admin: GET /admin/feedback (paginiert, Status-Filter) via „Mehr laden". */
export function useAdminFeedback(status: FeedbackStatus | 'ALL') {
    return useInfiniteQuery({
        queryKey: ['admin-feedback', status],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => {
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: String(FEEDBACK_PAGE_LIMIT),
            })
            if (status !== 'ALL') params.set('status', status)
            return apiRequest<FeedbackPage>(`/admin/feedback?${params}`)
        },
        getNextPageParam: lastPage =>
            lastPage.meta.page < lastPage.meta.totalPages
                ? lastPage.meta.page + 1
                : undefined,
    })
}

/** Admin: PATCH /admin/feedback/:id/status (OPEN/READ/DONE). */
export function useSetFeedbackStatus() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, status }: { id: number; status: FeedbackStatus }) =>
            apiRequest<Feedback>(`/admin/feedback/${id}/status`, {
                method: 'PATCH',
                body: { status },
            }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin-feedback'] })
        },
    })
}
